import { DateFormatter, Chip, CustomTooltip } from '@/shared/ui/components';
import { errorMarkIcon, eyeIcon } from '@/shared/ui/assets';
import { ARTIFACT_REPORT_HOVER_TEXT, DOUBLE_HYPHEN_BULLET } from '@/shared/config';
import { zeroPad, gettextReportType } from '@/shared/lib';
import _ from 'lodash';
import Skeleton from 'react-loading-skeleton';
import { t } from 'ttag';

/**
 * Renders the report identifier cell of the study reports table. End-of-use rows print the EoU label and interim rows a zero-padded id, both followed by an error-mark tooltip when the report is flagged as an artifact report, other rows fall back to the notification report id, the event friendly id or the raw cell value, and a skeleton is shown until the data has loaded.
 *
 * @param cell - Report id value for the row.
 * @param row - Report row supplying type, reportId, eventFid and the artifact flag.
 * @param rowIndex - Row position, unused.
 * @param formatExtraData - Table extra data supplying isLoadedData.
 * @returns The formatted report id cell or a loading skeleton.
 */
const reportIdFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { isLoadedData } = formatExtraData;
  const isEndOfUseReport = row.type === 'Study';
  const isInterimReport = row.type === 'Interim';
  const isEventReport = row.type === 'Event';
  return isLoadedData ? (
    <div>
      {isEndOfUseReport || isInterimReport ? (
        <div className="flex items-center">
          {isEndOfUseReport ? (
            <span>{t`EoU`}</span>
          ) : (
            <span>{zeroPad(row.type === 'Notification' ? row.reportId : cell)}</span>
          )}
          {row.isArtifactReport && (
            <div className="ml2">
              <CustomTooltip arrow={false} title={ARTIFACT_REPORT_HOVER_TEXT} placement="bottom">
                <img className="artifact-mark" src={errorMarkIcon} alt="Error mark icon" />
              </CustomTooltip>
            </div>
          )}
        </div>
      ) : (
        <span>
          {zeroPad(
            row.type === 'Notification' ? row.reportId : isEventReport ? row.eventFid : cell,
          )}
        </span>
      )}
    </div>
  ) : (
    <Skeleton count={1} />
  );
};

/**
 * Renders the date of service cell of the study reports table. Event rows go through the date formatter with the study start, timezone, study time offset and the facility's country and timezone at second precision, while all other report types print the raw cell value.
 *
 * @param cell - Preformatted date of service used for non-event rows.
 * @param row - Report row supplying type, study, start and facility data.
 * @param rowIndex - Row position, unused.
 * @param formatExtraData - Table extra data supplying timezone and studyTimeOffset.
 * @returns The formatted date of service cell.
 */
const dateOfServiceFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { timezone, studyTimeOffset } = formatExtraData;
  return row.type === 'Event' ? (
    <DateFormatter
      startStudyDate={row.study?.start}
      date={row.start}
      timezone={timezone}
      studyTimeOffset={studyTimeOffset}
      facilityCountry={row.facility?.contact?.country}
      facilityTimezone={row.facility?.timezone}
      format="MM/DD/YYYY HH:mm:ss"
    />
  ) : (
    <span>{cell}</span>
  );
};

/**
 * Renders the last modified cell of the study reports table. Once the table data has loaded the timestamp is rendered through the date formatter with the study start, timezone and facility context at second precision; before that a single-line skeleton is shown.
 *
 * @param cell - Last modified timestamp of the report.
 * @param row - Report row supplying study and facility context.
 * @param rowIndex - Row position, unused.
 * @param formatExtraData - Table extra data supplying isLoadedData and timezone.
 * @returns The formatted last modified cell or a loading skeleton.
 */
const lastModifiedWithActionFormatter = (
  cell: any,
  row: any,
  rowIndex: any,
  formatExtraData: any,
) => {
  const { isLoadedData, timezone } = formatExtraData;
  if (isLoadedData) {
    return (
      <DateFormatter
        startStudyDate={row.study?.start}
        date={cell}
        timezone={timezone}
        facilityCountry={row.facility?.contact?.country}
        facilityTimezone={row.facility?.timezone}
        facility={row.facility}
        format="MM/DD/YYYY HH:mm:ss"
      />
    );
  }

  return <Skeleton count={1} />;
};

/**
 * Renders the report type cell of the study reports table. The value is passed through the localized report-type helper once the data has loaded, otherwise a single-line skeleton is shown.
 *
 * @param cell - Raw report type of the row.
 * @param row - Report row, unused.
 * @param rowIndex - Row position, unused.
 * @param formatExtraData - Table extra data supplying isLoadedData.
 * @returns The localized report type cell or a loading skeleton.
 */
const reportTypeFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { isLoadedData } = formatExtraData;
  return isLoadedData ? <span>{gettextReportType(cell)}</span> : <Skeleton count={1} />;
};

/**
 * Renders the status cell of the study reports table as a colored chip. Completed statuses use the green palette and everything else the neutral grey one, a missing status falls back to the double-hyphen bullet, and a skeleton is shown until the data has loaded.
 *
 * @param cell - Report status of the row.
 * @param row - Report row, unused.
 * @param rowIndex - Row position, unused.
 * @param formatExtraData - Table extra data supplying isLoadedData.
 * @returns The status chip, a placeholder bullet or a loading skeleton.
 */
const statusFormatter = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { isLoadedData } = formatExtraData;
  const isCompletedStatus = cell === 'Completed';
  if (isLoadedData) {
    if (cell) {
      return (
        <Chip
          background={isCompletedStatus ? '#EDFAF0' : '#EAEDF1'}
          color={isCompletedStatus ? '#07A83D' : '#444F66'}
          closable={false}
          className="custom-chip"
        >
          {cell}
        </Chip>
      );
    }

    return DOUBLE_HYPHEN_BULLET;
  }

  return <Skeleton count={1} />;
};

/**
 * Renders the action cell of the study reports table as an eye icon. Clicking it forwards the whole row to the onClickReport callback taken from the table's extra data.
 *
 * @param cell - Cell value, unused.
 * @param row - Report row passed to the click callback.
 * @param rowIndex - Row position, unused.
 * @param formatExtraData - Table extra data supplying onClickReport.
 * @returns The clickable action cell.
 */
const actionFormater = (cell: any, row: any, rowIndex: any, formatExtraData: any) => {
  const { onClickReport } = formatExtraData;
  return (
    <div className="table-action">
      <img
        src={eyeIcon}
        alt="Eye icon"
        onClick={() => {
          onClickReport(row);
        }}
      />
    </div>
  );
};

/**
 * Comparator ordering study report rows by their identifier. Study rows are pushed to the end by taking the maximum safe integer, event rows compare by event friendly id and everything else by report id, with non-numeric values sorted ahead and equal values reported as ties.
 *
 * @param a - First report row being compared.
 * @param b - Second report row being compared.
 * @returns Negative, zero or positive ordering value for the two rows.
 */
const handleSortReportId = (a: any, b: any) => {
  const value1 =
    a.type === 'Study' ? Number.MAX_SAFE_INTEGER : a.type === 'Event' ? a.eventFid : a.reportId;
  const value2 =
    b.type === 'Study' ? Number.MAX_SAFE_INTEGER : b.type === 'Event' ? b.eventFid : b.reportId;

  if (!_.isNumber(value1)) {
    return -1;
  }

  if (!_.isNumber(value2)) {
    return 1;
  }

  if (value1 === value2) {
    return 0;
  }

  return value1 - value2;
};

export {
  actionFormater,
  dateOfServiceFormatter,
  handleSortReportId,
  lastModifiedWithActionFormatter,
  reportIdFormatter,
  reportTypeFormatter,
  statusFormatter,
};
